import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useModalStore } from '@/store';
import { neutralColors } from '@/constants/neutralColors';
import { trackMarketingEvent, MARKETING_ACTION } from '@/constants/utils/analyticsEvents';

import { useInteractive, useLanding } from './LandingKit';
import { isWeb, prefersReducedMotion } from './landingTokens';

/** Jobs Work actually ships (see SCENARIOS in HeroWorkShowcase and the agent skills). */
const EXAMPLE_GOALS = [
  'Build a website for my bakery and give me the files',
  'Turn my notes into a pitch deck for Friday',
  'Make a 30-second product video with music and a voiceover',
  'Research our competitors and put it in a spreadsheet',
  'Code a playable arcade game with a high score',
  'Write a business plan as a Word document',
];

const TYPE_MS = 28;
const HOLD_MS = 2200;
const ERASE_MS = 10;
const GAP_MS = 320;

/** Types each goal out, holds it, erases it, and moves on to the next. */
function useGoalCycle(enabled) {
  const [text, setText] = useState(EXAMPLE_GOALS[0]);

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = 0;
    const schedule = (fn, ms) => { timer = setTimeout(fn, ms); };
    const run = (index, typed, erasing) => {
      const goal = EXAMPLE_GOALS[index];
      if (!erasing && typed < goal.length) {
        setText(goal.slice(0, typed + 1));
        schedule(() => run(index, typed + 1, false), TYPE_MS);
      } else if (!erasing) {
        schedule(() => run(index, typed, true), HOLD_MS);
      } else if (typed > 0) {
        setText(goal.slice(0, typed - 1));
        schedule(() => run(index, typed - 1, true), ERASE_MS);
      } else {
        schedule(() => run((index + 1) % EXAMPLE_GOALS.length, 0, false), GAP_MS);
      }
    };
    setText('');
    schedule(() => run(0, 0, false), GAP_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  return text;
}

/**
 * A replica of the app's home composer (ChatInputFloating) with Work examples
 * typing into it. Nothing inside is editable: the whole block is one tap
 * target that opens account creation.
 */
function LandingHeroComposer({ style }) {
  const { isXS, isDark } = useLanding();
  const colors = neutralColors({}, isDark);
  const router = useRouter();
  const openModal = useModalStore((state) => state.actions.openModal);
  const { hovered, handlers } = useInteractive();
  const goal = useGoalCycle(isWeb && !prefersReducedMotion());

  const handlePress = useCallback(() => {
    trackMarketingEvent(MARKETING_ACTION.CTA_CLICKED, 'home/hero_composer/signup');
    if (isWeb && !isXS) {
      openModal('SignupModal');
      return;
    }
    router.push('/signup');
  }, [isXS, openModal, router]);

  return (
    <Pressable
      {...handlers}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel="Sign up free to put Gab AI to work"
      style={{
        width: '100%',
        maxWidth: 680,
        alignSelf: 'center',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: colors.border_color_primary,
        backgroundColor: colors.solid_color_primary,
        overflow: 'hidden',
        ...(isWeb ? {
          cursor: 'text',
          boxShadow: isDark ? 'none' : '0 4px 16px rgba(0,0,0,0.04)',
        } : {}),
        ...style,
      }}
    >
      <View aria-hidden importantForAccessibility="no-hide-descendants" style={{ minHeight: isXS ? 69 : 46, paddingVertical: 12, paddingLeft: 16, paddingRight: 12 }}>
        <Text numberOfLines={isXS ? 2 : 1} style={{ color: colors.text_color_primary, fontSize: 15, lineHeight: 22.5 }}>
          {goal}
          <Text style={{ color: colors.text_color_secondary, fontSize: 15, lineHeight: 22.5 }}>|</Text>
        </Text>
      </View>
      <View
        aria-hidden
        importantForAccessibility="no-hide-descendants"
        style={{ height: isXS ? 46 : 40, paddingTop: isXS ? 6 : 0, paddingHorizontal: 6, paddingBottom: 6, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <View style={{ width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="plus" size={16} color={colors.text_color_secondary} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <View style={{ height: 34, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
            <Text style={{ color: colors.text_color_secondary, fontSize: 13 }}>Auto</Text>
            <Icon name="chevron-down" size={8} color={colors.text_color_tertiary} />
          </View>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 20,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.accent_color_primary,
              opacity: hovered ? 0.85 : 1,
              ...(isWeb ? { transition: 'opacity 0.2s ease' } : {}),
            }}
          >
            <Icon name="arrow-up" size={16} color={colors.accent_text_color} />
          </View>
        </View>
      </View>
    </Pressable>
  );
}

export default React.memo(LandingHeroComposer);
